import React, { useState } from 'react';
import { financialTransactionAPI } from '../financialTransactionService';
import toast from 'react-hot-toast';
import { X, Loader, Heart, User, UserX } from 'lucide-react';
import { btn } from '../../utils/buttonStyles';

const CURRENT_YEAR = new Date().getFullYear();
const SEASONS = ['Year', 'H1', 'H2', 'Q1', 'Q2', 'Q3', 'Q4', 'Spring', 'Summer', 'Fall', 'Winter'];

const AddDonationModal = ({ groupId, adminId, members, onClose, onSaved, theme }) => {
  const [donorType, setDonorType]     = useState('anonymous'); // 'anonymous' | 'member' | 'external'
  const [donorName, setDonorName]     = useState('');
  const [donorUserId, setDonorUserId] = useState('');
  const [amount, setAmount]           = useState('');
  const [note, setNote]               = useState('');
  const [season, setSeason]           = useState('Year');
  const [seasonYear, setSeasonYear]   = useState(CURRENT_YEAR);
  const [saving, setSaving]           = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    const amt = parseFloat(amount);
    if (isNaN(amt) || amt <= 0) { toast.error('Enter a valid amount'); return; }
    if (donorType === 'external' && !donorName.trim()) { toast.error('Enter donor name'); return; }

    setSaving(true);
    try {
      const resolvedUserId = donorType === 'member' ? donorUserId : null;
      const resolvedName   =
        donorType === 'member'   ? members?.find(m => String(m.userId) === donorUserId)?.user?.firstName || 'Member'
        : donorType === 'external' ? donorName.trim()
        : null;

      await financialTransactionAPI.createDonation({
        groupId,
        adminId,
        amount: amt,
        donorUserId: resolvedUserId,
        donorName: resolvedName,
        note: note.trim() || null,
        season,
        seasonYear,
      });
      toast.success('Donation recorded');
      onSaved?.();
    } catch (err) {
      toast.error(err.message || 'Failed to record donation');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
      <div className={`${theme.card} rounded-2xl border ${theme.border} w-full max-w-sm shadow-2xl`}>
        {/* Header */}
        <div className={`flex items-center justify-between px-5 py-4 border-b ${theme.border}`}>
          <div className="flex items-center gap-2">
            <Heart className="w-4 h-4 text-pink-500" />
            <h2 className={`font-semibold ${theme.text}`}>Record Donation</h2>
          </div>
          <button onClick={onClose} className={`p-1.5 rounded-lg ${theme.textSecondary} hover:text-red-400 transition-colors`}>
            <X className="w-4 h-4" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-5 space-y-4">
          {/* Donor Type */}
          <div>
            <label className={`block text-xs font-semibold uppercase tracking-wide ${theme.textSecondary} mb-2`}>
              Donor
            </label>
            <div className="grid grid-cols-3 gap-2">
              {[
                { id: 'anonymous', label: 'Anonymous', icon: <UserX className="w-3.5 h-3.5" /> },
                { id: 'member',   label: 'Member',    icon: <User className="w-3.5 h-3.5" /> },
                { id: 'external', label: 'External',  icon: <Heart className="w-3.5 h-3.5" /> },
              ].map(opt => (
                <button
                  key={opt.id}
                  type="button"
                  onClick={() => setDonorType(opt.id)}
                  className={`flex items-center justify-center gap-1.5 py-2 rounded-xl text-xs font-semibold border transition-all ${
                    donorType === opt.id
                      ? 'bg-pink-500/20 text-pink-500 border-pink-500/40'
                      : `${theme.backgroundSecondary} ${theme.textSecondary} ${theme.border}`
                  }`}
                >
                  {opt.icon}{opt.label}
                </button>
              ))}
            </div>
          </div>

          {/* Member picker */}
          {donorType === 'member' && (
            <div>
              <label className={`block text-xs font-semibold uppercase tracking-wide ${theme.textSecondary} mb-1.5`}>
                Select Member
              </label>
              <select
                value={donorUserId}
                onChange={e => setDonorUserId(e.target.value)}
                required
                className={`w-full px-3 py-2.5 rounded-xl border ${theme.border} ${theme.card} ${theme.text} text-sm focus:outline-none focus:ring-2 focus:ring-pink-500/40`}
              >
                <option value="">Select a member…</option>
                {(members || []).map(m => (
                  <option key={m.userId} value={m.userId}>
                    {m.user?.firstName} {m.user?.lastName}
                  </option>
                ))}
              </select>
            </div>
          )}

          {/* External donor name */}
          {donorType === 'external' && (
            <div>
              <label className={`block text-xs font-semibold uppercase tracking-wide ${theme.textSecondary} mb-1.5`}>
                Donor Name
              </label>
              <input
                type="text"
                placeholder="e.g. John Smith"
                value={donorName}
                onChange={e => setDonorName(e.target.value)}
                className={`w-full px-3 py-2.5 rounded-xl border ${theme.border} ${theme.card} ${theme.text} text-sm focus:outline-none focus:ring-2 focus:ring-pink-500/40`}
              />
            </div>
          )}

          {/* Amount */}
          <div>
            <label className={`block text-xs font-semibold uppercase tracking-wide ${theme.textSecondary} mb-1.5`}>
              Amount ($)
            </label>
            <input
              type="number"
              min="0.01"
              step="0.01"
              placeholder="0.00"
              value={amount}
              onChange={e => setAmount(e.target.value)}
              className={`w-full px-3 py-2.5 rounded-xl border ${theme.border} ${theme.card} ${theme.text} text-sm focus:outline-none focus:ring-2 focus:ring-pink-500/40`}
            />
          </div>

          {/* Note */}
          <div>
            <label className={`block text-xs font-semibold uppercase tracking-wide ${theme.textSecondary} mb-1.5`}>
              Note <span className={theme.textSecondary}>(optional)</span>
            </label>
            <input
              type="text"
              placeholder="e.g. For new equipment"
              value={note}
              onChange={e => setNote(e.target.value)}
              className={`w-full px-3 py-2.5 rounded-xl border ${theme.border} ${theme.card} ${theme.text} text-sm focus:outline-none focus:ring-2 focus:ring-pink-500/40`}
            />
          </div>

          {/* Season + Year */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className={`block text-xs font-semibold uppercase tracking-wide ${theme.textSecondary} mb-1.5`}>Season</label>
              <select
                value={season}
                onChange={e => setSeason(e.target.value)}
                className={`w-full px-3 py-2.5 rounded-xl border ${theme.border} ${theme.card} ${theme.text} text-sm focus:outline-none focus:ring-2 focus:ring-pink-500/40`}
              >
                {SEASONS.map(s => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>
            <div>
              <label className={`block text-xs font-semibold uppercase tracking-wide ${theme.textSecondary} mb-1.5`}>Year</label>
              <input
                type="number"
                value={seasonYear}
                onChange={e => {
                  const val = e.target.value;
                  setSeasonYear(val === '' ? '' : parseInt(val));
                }}
                onBlur={e => {
                  if (e.target.value === '' || isNaN(parseInt(e.target.value))) {
                    setSeasonYear(CURRENT_YEAR);
                  }
                }}
                className={`w-full px-3 py-2.5 rounded-xl border ${theme.border} ${theme.card} ${theme.text} text-sm focus:outline-none focus:ring-2 focus:ring-pink-500/40`}
              />
            </div>
          </div>

          {/* Actions */}
          <div className="flex gap-3 pt-1">
            <button type="button" onClick={onClose} className={`${btn.base} ${btn.md} ${theme.buttonGhost} flex-1`}>
              Cancel
            </button>
            <button
              type="submit"
              disabled={saving}
              className={`${btn.base} ${btn.md} ${theme.buttonPrimary} flex-1 flex items-center justify-center gap-2`}
            >
              {saving && <Loader className="w-4 h-4 animate-spin" />}
              Record Donation
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AddDonationModal;
